/**
 * Customer Catalog UI types — backend SKU rows are mapped here by the page.
 */

export type ProductStatus = 'in-stock' | 'out-of-stock' | 'low-stock';

export interface CatalogProduct {
  id: string;
  name: string;
  sku: string;
  packSize: string;
  /** Real, tier-resolved customer price (VP-CUST-002). */
  price: number;
  /** List price, shown struck-through when it differs from `price`. */
  originalPrice?: number;
  image?: string;
  status: ProductStatus;
  category: string;
  isPreviouslyOrdered: boolean;
  isNew: boolean;
  isDeal: boolean;
}

export type SortOption = 'popularity' | 'price-low' | 'price-high' | 'name-az' | 'name-za';

export type CatalogQuickFilter = 'all' | 'favorites' | 'previously-ordered' | 'deals' | 'new';

export const quickFilterOptions: { label: string; value: CatalogQuickFilter }[] = [
  { label: 'All Products', value: 'all' },
  { label: 'Favorites', value: 'favorites' },
  { label: 'Previously Ordered', value: 'previously-ordered' },
  { label: 'Deals', value: 'deals' },
  { label: 'New', value: 'new' },
];

export const sortOptions: { label: string; value: SortOption }[] = [
  { label: 'Sort by: Popularity', value: 'popularity' },
  { label: 'Sort by: Price (Low to High)', value: 'price-low' },
  { label: 'Sort by: Price (High to Low)', value: 'price-high' },
  { label: 'Sort by: Name (A-Z)', value: 'name-az' },
  { label: 'Sort by: Name (Z-A)', value: 'name-za' },
];

/**
 * Static category fallback used when categories haven't loaded yet.
 * Real categories are sourced from useCategoriesStore at the page level.
 */
export const categoryOptions: string[] = ['All Categories'];

export function sortProducts(products: CatalogProduct[], sort: SortOption): CatalogProduct[] {
  const sorted = [...products];
  switch (sort) {
    case 'price-low': return sorted.sort((a, b) => a.price - b.price);
    case 'price-high': return sorted.sort((a, b) => b.price - a.price);
    case 'name-az': return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'name-za': return sorted.sort((a, b) => b.name.localeCompare(a.name));
    case 'popularity':
    default: return sorted;
  }
}
